import type { ExtractionCitation } from '@ctw/shared/api/ai/use-extraction-jobs';
import type { ExportField } from '@ctw/shared/content/fact-extraction/compute-export-summary';

// RFC 4180: quote fields containing commas, quotes, or line breaks; double
// internal quotes. Clinical values regularly contain all three.
const CSV_FIELD_REGEX = /[",\r\n]/;

const escapeCsvField = (field: string): string =>
  CSV_FIELD_REGEX.test(field) ? `"${field.replace(/"/g, '""')}"` : field;

// Spreadsheet apps execute cells beginning with = + - @ (or tab/CR) as
// formulas, and values here originate in external clinical documents — the
// classic CSV-injection vector. Guard with a leading apostrophe (the OWASP
// mitigation); Excel/Sheets hide it, machine consumers must treat a leading
// apostrophe as a guard character. Leading whitespace is stripped by
// spreadsheet parsers before formula evaluation, so a trigger hidden behind
// spaces/tabs (" =1+1") is guarded too; a standalone leading tab/CR is itself
// a trigger.
const FORMULA_PREFIX_REGEX = /^\s*[=+\-@]|^[\t\r]/;

const guardFormulaInjection = (field: string): string =>
  FORMULA_PREFIX_REGEX.test(field) ? `'${field}` : field;

const toCsvCell = (raw: string): string => escapeCsvField(guardFormulaInjection(raw));

const CSV_HEADER = 'group,field,value,documented,citations,sourceDocuments';

const EMPTY_FILE_NAMES: ReadonlyMap<string, string> = new Map();

/**
 * The canonical Atlas export CSV. This format is a contract:
 * column changes require the same care as an API change, and the
 * golden-file test pins the exact output. Undocumented fields export with an
 * empty value rather than the extraction generator's "not documented"
 * sentinel so consumers never parse magic strings.
 *
 * `sourceDocuments` lists the zip's `documents/` file names (deduped,
 * semicolon-joined) backing the row's citations, keyed by citation ref.
 * Callers populate `fileNameByCitationRef` from the export's actual document
 * results, so the column only ever references files present in the zip —
 * documents that failed or were unsupported contribute nothing here (the
 * manifest records why).
 */
export function generateAtlasCsv({
  fields,
  citations,
  fileNameByCitationRef = EMPTY_FILE_NAMES,
}: {
  fields: Array<ExportField>;
  citations: Array<ExtractionCitation>;
  fileNameByCitationRef?: ReadonlyMap<string, string>;
}): string {
  const citationById = new Map(citations.map((citation) => [citation.id, citation]));
  const rows = fields.map((field) => {
    const citationRefs = field.isDocumented
      ? field.citationIds
          .map((citationId) => citationById.get(citationId))
          .filter((citation) => citation !== undefined)
          .map((citation) => `${citation.resourceType}/${citation.resourceId}`)
      : [];
    const sourceDocuments = [
      ...new Set(
        citationRefs
          .map((ref) => fileNameByCitationRef.get(ref))
          .filter((fileName) => fileName !== undefined),
      ),
    ].join(';');
    return [
      toCsvCell(field.groupLabel),
      toCsvCell(field.label),
      toCsvCell(field.isDocumented ? field.value : ''),
      String(field.isDocumented),
      toCsvCell(citationRefs.join(';')),
      toCsvCell(sourceDocuments),
    ].join(',');
  });
  return [CSV_HEADER, ...rows].map((row) => `${row}\r\n`).join('');
}
